import type { KeyboardEvent, ReactNode } from 'react';

import { CloseLineIcon, MoveIcon } from '../../layout/icons';
import { useDraggableChrome } from '../../layout/useDraggableChrome';
import type { ResultPanelTab } from './sessions';
import '../../styles/rail-drag.css';
import '../../styles/quiz.css';
import '../../styles/submissions.css';

const TABS: Array<{ id: ResultPanelTab; label: string }> = [
  { id: 'report', label: 'Báo cáo' },
  { id: 'detail', label: 'Chi tiết' },
];

export interface ResultsCardFrameProps {
  /** Accessible name of the card. */
  label: string;
  subtitle: string;
  /** The đề actions, on their own row under the title. */
  actions: ReactNode;
  tab: ResultPanelTab;
  onTab: (tab: ResultPanelTab) => void;
  onClose: () => void;
  children: ReactNode;
}

// The floating Kết quả card: title row (title, subtitle, close), action row, Báo cáo | Chi tiết
export function ResultsCardFrame({ label, subtitle, actions, tab, onTab, onClose, children }: ResultsCardFrameProps) {
  // one saved spot for every results card, exam or homework
  const drag = useDraggableChrome({ id: 'exam-results', enabled: true });
  const className = ['quiz-results quiz-results--floating', drag.dragging ? 'quiz-results--dragging' : ''].filter(Boolean).join(' ');

  function onKey(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    onTab(tab === 'report' ? 'detail' : 'report');
  }

  return (
    <aside ref={drag.ref} className={className} aria-label={label}>
      <header className="quiz-results__header">
        <button type="button" className="quiz-results__handle" aria-label="Kéo để di chuyển bảng kết quả, dùng phím mũi tên để chỉnh vị trí" {...drag.handleProps}>
          <MoveIcon size={18} />
        </button>
        <div className="quiz-results__heading">
          <div className="quiz-results__title">Kết quả</div>
          <div className="quiz-results__subtitle" title={subtitle}>{subtitle}</div>
        </div>
        <button type="button" className="quiz-results__close" aria-label="Đóng bảng kết quả" onClick={onClose}>
          <CloseLineIcon size={18} />
        </button>
      </header>
      <div className="quiz-results__toolbar">{actions}</div>
      <div className="up-tabs quiz-results__tabs" role="tablist" aria-label="Nội dung kết quả">
        {TABS.map((item) => (
          <button key={item.id} type="button" role="tab" id={`results-tab-${item.id}`} aria-selected={tab === item.id} aria-controls="results-tabpanel" tabIndex={tab === item.id ? 0 : -1} className={tab === item.id ? 'is-active' : ''} onKeyDown={onKey} onClick={() => onTab(item.id)}>{item.label}</button>
        ))}
      </div>
      <div className="quiz-results__scroll" role="tabpanel" id="results-tabpanel" aria-labelledby={`results-tab-${tab}`}>{children}</div>
    </aside>
  );
}
